import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";

import { useSaveRegistration, useSubmitRegistration } from "../../../api/hooks";
import type { RegistrationProfile } from "../../../types";
import { CheckRow, TextRow } from "./controls";

// The organization step's required text fields, as the submit endpoint names
// them in a refusal (registration_sync.REQUIRED_ORGANIZATION_TEXT_FIELDS).
const ORGANIZATION_FIELDS = [
  "organization_name",
  "neq",
  "description",
  "contact_primary_last_name",
  "contact_primary_first_name",
  "contact_primary_function",
  "contact_primary_email",
  "contact_primary_phone",
];

/**
 * Final declaration + consent, in a modal opened from the goals step's
 * "Submit my request" button (parity with the legacy app, where the declaration
 * lives in a modal on the last form step — not a separate step). The declaration
 * fields are PATCHed, then (for a draft/rejected profile) the registration is
 * submitted; a finalized profile's edits are saved (and staged server-side for a
 * validated expert) without resubmitting.
 *
 * The modal opens on the signature sentence and asks for one consent — that the
 * CQDD analyses the request. There is no signature box: submitting is the
 * signature, recorded server-side (the same modal as the Django step).
 *
 * A submit refused over a blank organization field cannot be fixed here, so the
 * modal hands the server's message to `onOrganizationIncomplete`, which sends
 * the expert back to that step — as Django redirects there with the same words.
 */
export function DeclarationModal({
  profile,
  isEdit,
  onSubmitted,
  onClose,
  onOrganizationIncomplete,
}: {
  profile: RegistrationProfile;
  isEdit: boolean;
  onSubmitted: () => void;
  onClose: () => void;
  onOrganizationIncomplete?: (message: string) => void;
}) {
  const { t } = useTranslation();
  const save = useSaveRegistration();
  const submit = useSubmitRegistration();
  const [declarantName, setDeclarantName] = useState(
    profile.declarant_name ?? "",
  );
  const [declarantFunction, setDeclarantFunction] = useState(
    profile.declarant_function ?? "",
  );
  const [consent, setConsent] = useState(Boolean(profile.consent_display));
  const [error, setError] = useState<string | null>(null);

  const canSubmit = declarantName.trim() && declarantFunction.trim() && consent;

  const handleSubmit = () => {
    setError(null);
    save.mutate(
      {
        declarant_name: declarantName,
        declarant_function: declarantFunction,
        consent_display: consent,
      },
      {
        onSuccess: () => {
          if (isEdit) {
            onSubmitted();
            return;
          }
          submit.mutate(undefined, {
            onSuccess: () => onSubmitted(),
            onError: (err: unknown) => {
              const data = (err ?? {}) as Record<string, unknown>;
              const detail =
                typeof data.detail === "string"
                  ? data.detail
                  : t("auth.genericError");
              if (
                onOrganizationIncomplete &&
                ORGANIZATION_FIELDS.some((field) => field in data)
              ) {
                onOrganizationIncomplete(detail);
                return;
              }
              setError(detail);
            },
          });
        },
        onError: () => setError(t("auth.genericError")),
      },
    );
  };

  const busy = save.isPending || submit.isPending;

  return (
    <Modal title={t("expert.register.declarationTitle")} onClose={onClose}>
      <div className="space-y-5">
        <p className="text-sm font-medium">
          {t("expert.register.signatureStatement")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("expert.register.mandatoryNote")}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextRow
            label={t("expert.register.fields.declarant_name")}
            value={declarantName}
            onChange={setDeclarantName}
            required
          />
          <TextRow
            label={t("expert.register.fields.declarant_function")}
            value={declarantFunction}
            onChange={setDeclarantFunction}
            required
          />
        </div>
        <CheckRow
          label={t("expert.register.fields.consent_display")}
          checked={consent}
          onChange={setConsent}
          required
        />

        {error && (
          <p className="text-sm font-medium text-destructive">{error}</p>
        )}

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {t("expert.register.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || busy}>
            {isEdit
              ? t("expert.register.confirmSave")
              : t("expert.register.confirmSubmit")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
